import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BuildingOffice2Icon, UserIcon } from '@heroicons/react/24/outline';
import authStore from '../store/authStore';

const providerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  practiceName: z.string().min(1, 'Practice name is required').max(255),
  email: z.string().email('Enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

function AccountTypeSelector({ onSelect }) {
  return (
    <div className="space-y-4">
      <p className="text-center text-neutral-600 dark:text-neutral-400 mb-6">
        How will you be using Sage?
      </p>
      <button
        type="button"
        onClick={() => onSelect('provider')}
        className="w-full flex items-start gap-4 p-4 border-2 border-neutral-200 dark:border-neutral-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
      >
        <BuildingOffice2Icon className="w-8 h-8 text-primary-600 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold text-neutral-900 dark:text-neutral-100">I&apos;m a provider</p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Therapist, counselor, or practice administrator setting up a practice
          </p>
        </div>
      </button>
      <button
        type="button"
        onClick={() => onSelect('client')}
        className="w-full flex items-start gap-4 p-4 border-2 border-neutral-200 dark:border-neutral-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
      >
        <UserIcon className="w-8 h-8 text-primary-600 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold text-neutral-900 dark:text-neutral-100">I&apos;m a client / patient</p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Accessing my portal to view appointments, documents, and messages
          </p>
        </div>
      </button>
    </div>
  );
}

function ClientInfo() {
  return (
    <div className="text-center space-y-4">
      <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto">
        <UserIcon className="w-8 h-8 text-primary-600" />
      </div>
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
        Check your email for an invitation
      </h2>
      <p className="text-neutral-600 dark:text-neutral-400 text-sm">
        Client portal accounts are created by your provider. If your therapist or counselor
        has your email address on file, you should receive an invitation link automatically
        when they add you to their practice.
      </p>
      <p className="text-neutral-600 dark:text-neutral-400 text-sm">
        If you haven&apos;t received an invitation, please contact your provider directly and ask
        them to send you a portal invite.
      </p>
      <div className="pt-2">
        <Link to="/login" className="btn btn-primary w-full block text-center">
          Sign in with existing account
        </Link>
      </div>
    </div>
  );
}

function ProviderForm() {
  const navigate = useNavigate();
  const register = authStore((s) => s.register);

  const {
    register: field,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(providerSchema) });

  const onSubmit = async ({ firstName, lastName, practiceName, email, password }) => {
    try {
      await register({ firstName, lastName, practiceName, email, password, role: 'practitioner' });
      toast.success('Account created! Please check your email to verify your account.');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">First name</label>
          <input type="text" className="input" placeholder="Jane" {...field('firstName')} />
          {errors.firstName && (
            <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.firstName.message}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Last name</label>
          <input type="text" className="input" placeholder="Smith" {...field('lastName')} />
          {errors.lastName && (
            <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Practice name</label>
        <input
          type="text"
          className="input"
          placeholder="Sunrise Therapy"
          {...field('practiceName')}
        />
        {errors.practiceName && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.practiceName.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Email</label>
        <input
          type="email"
          className="input"
          placeholder="you@example.com"
          {...field('email')}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Password</label>
        <input
          type="password"
          className="input"
          placeholder="••••••••"
          {...field('password')}
        />
        {errors.password && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.password.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Confirm password</label>
        <input
          type="password"
          className="input"
          placeholder="••••••••"
          {...field('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.confirmPassword.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn btn-primary w-full disabled:opacity-60"
      >
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </button>
    </form>
  );
}

export default function Register() {
  const [accountType, setAccountType] = useState(null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-md w-full card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 mb-2">Sage</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {accountType === 'provider' ? 'Create your provider account' : 'Create your account'}
          </p>
        </div>

        {accountType === null && (
          <AccountTypeSelector onSelect={setAccountType} />
        )}
        {accountType === 'provider' && <ProviderForm />}
        {accountType === 'client' && <ClientInfo />}

        {accountType !== null && (
          <button
            type="button"
            onClick={() => setAccountType(null)}
            className="mt-4 text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 w-full text-center"
          >
            ← Back
          </button>
        )}

        {accountType !== 'client' && (
          <p className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
